import React, { useMemo, useState } from 'react';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

type UserType = 'individual' | 'freelancer' | 'small_company';

export const WaitlistModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSignIn?: () => void;
}> = ({ isOpen, onClose, onSignIn }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [userType, setUserType] = useState<UserType | ''>('');
  const [note, setNote] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const canSubmit = useMemo(() => {
    return !!email.trim() && !!name.trim() && !!userType && !loading;
  }, [email, name, userType, loading]);

  if (!isOpen) return null;

  const reset = () => {
    setEmail('');
    setName('');
    setPhone('');
    setUserType('');
    setNote('');
    setError(null);
    setSuccess(false);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const openSignIn = () => {
    handleClose();
    onSignIn?.();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !name.trim() || !userType) {
      setError('Please fill the required fields.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/waitlist', {
        email: email.trim(),
        name: name.trim(),
        phone: phone.trim() || null,
        user_type: userType,
        // Keep only one optional free-form question (matches UI). Stored as why_need.
        why_need: note.trim() || null,
        what_do: null,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to join waitlist'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      <div className="relative w-full max-w-xl max-h-[85vh] rounded-2xl bg-white shadow-2xl border border-purple-200 overflow-hidden flex flex-col">
        <div className="p-5 sm:p-6 bg-gradient-to-br from-purple-600 via-purple-500 to-purple-400 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4h16v16H4z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M22 6l-10 7L2 6" />
              </svg>
            </div>
            <div>
              <div className="text-2xl font-black text-white">Join the Waitlist</div>
              <div className="text-purple-100 text-sm">Get early access to Lead Pilot</div>
            </div>
          </div>
          <button onClick={handleClose} className="text-white/90 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 sm:p-6 overflow-y-auto bg-white">
          {success ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5">
              <div className="text-lg font-black text-slate-900">You’re on the list.</div>
              <div className="text-emerald-700 text-sm mt-1">
                We’ll reach out when your beta invite is ready.
              </div>
              <button
                onClick={handleClose}
                className="mt-5 w-full py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-rose-700 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-bold mb-2 text-slate-700">
                  Name <span className="text-purple-600">*</span>
                </label>
                <input
                  className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 placeholder:text-slate-400 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-slate-700">
                  Email address <span className="text-purple-600">*</span>
                </label>
                <input
                  className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 placeholder:text-slate-400 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-slate-700">Phone (optional)</label>
                <input
                  className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 placeholder:text-slate-400 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-slate-700">
                  What best describes you? <span className="text-purple-600">*</span>
                </label>
                <select
                  className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                  value={userType}
                  onChange={(e) => setUserType(e.target.value as any)}
                  required
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  <option value="individual">Individual</option>
                  <option value="freelancer">Freelancer</option>
                  <option value="small_company">Small company</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-slate-700">Optional note</label>
                <textarea
                  className="w-full min-h-[72px] rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 placeholder:text-slate-400 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything you’d like Lead Pilot to help you with?"
                />
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting…' : 'Join the Waitlist'}
              </button>

              <div className="text-center text-xs text-slate-500">
                We’ll never share your email. Unsubscribe anytime.
              </div>

              <div className="text-center text-xs text-slate-500">
                Already invited?{' '}
                <button
                  type="button"
                  onClick={openSignIn}
                  className="text-purple-600 font-bold hover:text-purple-700 underline underline-offset-2"
                >
                  Sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

